import React, { useState, useEffect, useRef, useCallback } from 'react';

import { fetchFipeBrands, fetchFipeModels } from '../../../api/fipeApi';
import FipePickerModal, { PickerItem } from '../brand-picker/BrandPickerModal';

import {
  CarClass,
  CarClassMap,
  getCarClassKeyFromLabel,
  DamageType,
  DamageTypeMap,
  getDamageTypeKeyFromLabel,
} from '../../mappings/textMappings';
import { OBSERVACOES_MAX_LENGTH } from '../../../utils/textLimits';
import { formatPhoneBr } from '../../../utils/phoneMask';

import { toast } from 'react-toastify';
import 'react-datepicker/dist/react-datepicker.css';
import './EditCarModal.css';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';

interface CarFormData {
  plate: string;
  chassis: string;
  model?: string;
  entryDate: string;
  eventNumber?: number;
  carClass?: string;
  damageType?: string;
  statusUpdatedAt?: string;
  color: string;
  /** Somente estado interno do formulário — não é aceito pelo UpdateCarDto e não deve ir no payload. */
  status?: string;
  esteiraId?: string;
  externalShopDays?: number;
  modelYear?: number;
  anoFabricacao?: number;
  marca?: string;
  fipeBrandCode?: string;
  fipeModelCode?: string;
  renavam?: string;
  numeroBo?: string;
  ownerName?: string;
  ownerPhone?: string;
  /** at-76: IMEI do rastreador. String vazia limpa o campo. */
  rastreadorImei?: string;
  /** at-54: relatos e combinados com o cliente. String vazia limpa o campo. */
  observacoesCliente?: string;
  /** at-54: anotações internas da equipe. String vazia limpa o campo. */
  observacoesOficina?: string;
}

interface EditCarModalProps {
  isOpen: boolean;
  onCancel: () => void;
  carDetails: any;
  onUpdate: (id: string, data: CarFormData) => Promise<void>;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE = Array.from({ length: CURRENT_YEAR - 1989 }, (_, i) => CURRENT_YEAR - i);

const EditCarModal: React.FC<EditCarModalProps> = ({
  isOpen,
  onCancel,
  carDetails,
  onUpdate,
  sidebarRef,
}) => {
  const [formData, setFormData] = useState<CarFormData>({
    plate: '', chassis: '', model: '', entryDate: '', eventNumber: 0,
    carClass: '', damageType: '', statusUpdatedAt: '',
    color: '', status: '', externalShopDays: 0, modelYear: undefined,
    anoFabricacao: undefined, marca: undefined, fipeBrandCode: undefined, fipeModelCode: undefined,
    renavam: undefined, numeroBo: undefined, ownerName: '', ownerPhone: '', rastreadorImei: '',
    observacoesCliente: '', observacoesOficina: '',
  });

  // FIPE selection state
  const [vehicleType, setVehicleType] = useState<'car' | 'motorcycle'>('car');
  const [selectedBrandName, setSelectedBrandName] = useState('');
  const [selectedBrandCode, setSelectedBrandCode] = useState('');
  const [selectedModelName, setSelectedModelName] = useState('');
  const [showBrandPicker, setShowBrandPicker] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);


  const modalRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(false);

  const anoFabricacao = formData.anoFabricacao;
  const modelYear = formData.modelYear;

  // Ano do modelo pode ser o mesmo ano de fabricação ou o ano seguinte
  const compatibleModelYears: number[] =
    anoFabricacao === undefined
      ? []
      : [anoFabricacao, anoFabricacao + 1].filter((y) => y <= CURRENT_YEAR + 1);

  const yearsReady = anoFabricacao !== undefined && modelYear !== undefined;
  const brandReady = yearsReady && selectedBrandCode !== '';

  const fetchBrands = useCallback(
    ({ search, page, limit }: { search?: string; page: number; limit: number }) =>
      fetchFipeBrands({ type: vehicleType, modelYear, search, page, limit }) as unknown as Promise<{ totalItems: number; items: PickerItem[] }>,
    [vehicleType, modelYear],
  );

  const fetchModels = useCallback(
    ({ search, page, limit }: { search?: string; page: number; limit: number }) =>
      fetchFipeModels({ brandCode: selectedBrandCode, type: vehicleType, modelYear, search, page, limit }) as unknown as Promise<{ totalItems: number; items: PickerItem[] }>,
    [selectedBrandCode, vehicleType, modelYear],
  );


  useEffect(() => {
    if (!carDetails) return;

    const carClassKey = Object.entries(CarClassMap).find(([, v]) => v === carDetails.carClass)?.[0] || carDetails.carClass;
    const damageTypeKey = Object.entries(DamageTypeMap).find(([, v]) => v === carDetails.damageType)?.[0] || carDetails.damageType;

    setFormData({
      plate: carDetails.plate,
      chassis: carDetails.chassis,
      model: carDetails.model,
      entryDate: carDetails.entryDate ? carDetails.entryDate.split('T')[0] : '',
      eventNumber: carDetails.eventNumber,
      carClass: carClassKey || '',
      damageType: damageTypeKey || '',
      statusUpdatedAt: carDetails.statusUpdatedAt,
      color: carDetails.color,
      status: carDetails.status,
      externalShopDays: carDetails.externalShopDays ?? 0,
      modelYear: carDetails.modelYear ?? undefined,
      anoFabricacao: carDetails.anoFabricacao ?? undefined,
      marca: carDetails.marca ?? undefined,
      fipeBrandCode: carDetails.fipeBrandCode ?? undefined,
      fipeModelCode: carDetails.fipeModelCode ?? undefined,
      renavam: carDetails.renavam ?? undefined,
      numeroBo: carDetails.numeroBo ?? undefined,
      ownerName: carDetails.ownerName ?? '',
      ownerPhone: formatPhoneBr(carDetails.ownerPhone ?? ''),
      rastreadorImei: carDetails.rastreadorImei ?? '',
      observacoesCliente: carDetails.observacoesCliente ?? '',
      observacoesOficina: carDetails.observacoesOficina ?? '',
    });

    setSelectedBrandName(carDetails.marca ?? '');
    setSelectedBrandCode(carDetails.fipeBrandCode ?? '');
    setSelectedModelName(carDetails.model ?? '');

  }, [carDetails]);

  const anyPickerOpenRef = useRef(false);
  useEffect(() => {
    anyPickerOpenRef.current = showBrandPicker || showModelPicker;
  }, [showBrandPicker, showModelPicker]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (anyPickerOpenRef.current) return;
      const node = event.target as Node;
      if (modalRef.current && !modalRef.current.contains(node) && !sidebarRef?.current?.contains(node)) onCancel();
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onCancel, sidebarRef]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'eventNumber' ? Number(value) : value }));
  };

  const handleBrandSelect = (item: PickerItem) => {
    setSelectedBrandName(item.name);
    setSelectedBrandCode(item.id); // using id as fallback; actual code is in item.code
    const code = (item as any).code ?? item.id;
    setSelectedBrandCode(code);
    setSelectedModelName('');
    setFormData((prev) => ({ ...prev, marca: item.name, fipeBrandCode: code, model: '', fipeModelCode: undefined }));
    setShowBrandPicker(false);
  };

  const handleModelSelect = (item: PickerItem) => {
    const code = (item as any).code ?? item.id;
    setSelectedModelName(item.name);
    setFormData((prev) => ({ ...prev, model: item.name, fipeModelCode: code }));
    setShowModelPicker(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Só a placa é obrigatória (decisão de 07/2026)
    if (!formData.plate) {
      toast.warning('Por favor, informe a placa do veículo.');
      setIsLoading(false);
      return;
    }

    const ownerPhoneDigits = (formData.ownerPhone ?? '').replace(/\D/g, '');
    if (ownerPhoneDigits && (ownerPhoneDigits.length < 10 || ownerPhoneDigits.length > 13)) {
      toast.warning('O telefone do proprietário deve conter DDD e número (10 a 13 dígitos).');
      setIsLoading(false);
      return;
    }
    // at-76: mesmo padrão do telefone — opcional, mas comprimento conferido antes do 400.
    const imeiDigits = (formData.rastreadorImei ?? '').replace(/\D/g, '');
    if (imeiDigits && (imeiDigits.length < 14 || imeiDigits.length > 17)) {
      toast.warning('O IMEI do rastreador deve ter de 14 a 17 dígitos.');
      setIsLoading(false);
      return;
    }

    const copy = formData;
    const originalDateOnly = carDetails.entryDate ? carDetails.entryDate.split('T')[0] : '';
    const hasDateChanged = originalDateOnly !== copy.entryDate;

    try {
      const updatedData: CarFormData = {
        plate: copy.plate,
        chassis: copy.chassis || '',
        model: copy.model || undefined,
        entryDate: hasDateChanged && copy.entryDate ? new Date(copy.entryDate).toISOString() : carDetails.entryDate,
        eventNumber: copy.eventNumber || undefined,
        color: copy.color,
        esteiraId: carDetails.esteiraId,
        carClass: copy.carClass ? getCarClassKeyFromLabel(copy.carClass) : undefined,
        damageType: copy.damageType ? getDamageTypeKeyFromLabel(copy.damageType) : undefined,
        externalShopDays: copy.externalShopDays ?? 0,
        modelYear: copy.modelYear ? parseInt(copy.modelYear.toString(), 10) : undefined,
        anoFabricacao: copy.anoFabricacao,
        marca: copy.marca,
        fipeBrandCode: copy.fipeBrandCode,
        fipeModelCode: copy.fipeModelCode,
        renavam: copy.renavam || undefined,
        numeroBo: copy.numeroBo || undefined,
        ownerName: (copy.ownerName ?? '').trim() || undefined,
        ownerPhone: ownerPhoneDigits || undefined,
        // String vazia LIMPA no backend; é o que permite corrigir documento errado (at-76).
        rastreadorImei: imeiDigits || '',
        // at-54: enviados sempre (inclusive vazios) — é assim que apagar o texto
        // limpa o campo no backend. `undefined` significaria "não mexer".
        observacoesCliente: copy.observacoesCliente ?? '',
        observacoesOficina: copy.observacoesOficina ?? '',
      };

      await onUpdate(carDetails.id, updatedData);

      toast.success('Veículo atualizado com sucesso');
      setTimeout(() => { setIsLoading(false); onCancel(); }, 300);
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error('Erro: A placa já está cadastrada para outro veículo.');
      } else {
        toast.error('Erro ao atualizar veículo. Tente novamente.');
      }
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="edit-car-modal-overlay edit-car-modal-specific">
      {isLoading && (
        <FullScreenLoader message="Salvando veículo..." />
      )}
      <div className="edit-car-modal-content" ref={modalRef}>
        <div className="Lightbox">
          <form onSubmit={handleSubmit} className="LightboxLogin">
            <div className="EditarCarro">Editar veículo</div>
            <div className="Frame1000004010">

              {/* Row 1: Placa, Chassi, Evento */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Placa</div>
                  <div className="CampoInput"><input className="Input" type="text" name="plate" value={formData.plate} onChange={handleChange} /></div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Chassi</div>
                  <div className="CampoInput"><input className="Input" type="text" name="chassis" value={formData.chassis} onChange={handleChange} /></div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Número do Evento</div>
                  <div className="DropDownFormulRio"><input className="Input" type="text" name="eventNumber" value={formData.eventNumber} onChange={handleChange} /></div>
                </div>
              </div>

              {/* Row 2: Tipo veículo, Ano fabricação, Ano modelo */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Tipo de veículo</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select className="Input" value={vehicleType} onChange={(e) => { setVehicleType(e.target.value as 'car' | 'motorcycle'); setSelectedBrandName(''); setSelectedBrandCode(''); setSelectedModelName(''); }}>
                        <option value="car">Carro</option>
                        <option value="motorcycle">Moto</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Ano de fabricação</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select
                        className="Input"
                        value={formData.anoFabricacao ?? ''}
                        onChange={(e) => {
                          const v = e.target.value === '' ? undefined : Number(e.target.value);
                          setFormData((prev) => ({ ...prev, anoFabricacao: v, modelYear: undefined }));
                          setSelectedBrandName(''); setSelectedBrandCode(''); setSelectedModelName('');
                        }}
                      >
                        <option value="">Selecione</option>
                        {YEAR_RANGE.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Ano do modelo</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select
                        className="Input"
                        value={formData.modelYear ?? ''}
                        onChange={(e) => {
                          const v = e.target.value === '' ? undefined : Number(e.target.value);
                          setFormData((prev) => ({ ...prev, modelYear: v }));
                          setSelectedBrandName(''); setSelectedBrandCode(''); setSelectedModelName('');
                        }}
                        disabled={formData.anoFabricacao === undefined}
                      >
                        <option value="">{formData.anoFabricacao === undefined ? 'Selecione o ano de fabricação primeiro' : 'Selecione'}</option>
                        {compatibleModelYears.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Marca, Modelo, Cor */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Marca</div>
                  <div className="CampoInput">
                    <button type="button" className="Input fipe-picker-btn" disabled={!yearsReady} onClick={() => setShowBrandPicker(true)}>
                      {selectedBrandName || (yearsReady ? 'Selecionar marca...' : 'Selecione os anos primeiro')}
                    </button>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Modelo</div>
                  <div className="CampoInput">
                    <button type="button" className="Input fipe-picker-btn" disabled={!brandReady} onClick={() => setShowModelPicker(true)}>
                      {selectedModelName || (brandReady ? 'Selecionar modelo...' : 'Selecione a marca primeiro')}
                    </button>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Cor</div>
                  <div className="DropDownFormulRio"><input className="Input" type="text" name="color" value={formData.color} onChange={handleChange} /></div>
                </div>
              </div>

              {/* Row 4: Classe, Tipo dano */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Classe do Veículo</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select className="Input" name="carClass" value={formData.carClass} onChange={handleChange}>
                        <option value="">Selecione a classe do veículo</option>
                        {Object.entries(CarClass).map(([key, value]) => <option key={key} value={value}>{CarClassMap[value as CarClass]}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Tipo do dano</div>
                  <div className="DropDownFormulRio">
                    <div className="InputSelect">
                      <select className="Input" name="damageType" value={formData.damageType} onChange={handleChange}>
                        <option value="">Selecione o tipo de dano</option>
                        {Object.entries(DamageType).map(([key, value]) => <option key={key} value={value}>{DamageTypeMap[value as DamageType]}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dynamic tags */}
              {/* at-89: as tags de esteira saíram daqui (decisão do Victor, 03/09/2026) — hoje
                  são editadas pela ação "Atualizar Motivo da Pendência", no modal do veículo,
                  que mostra só as da esteira ATUAL. Este formulário listava as de TODAS as
                  esteiras, sem dizer a qual cada uma pertencia. */}

              {/* Row 5: Data entrada, Dias oficina externa */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Data de entrada</div>
                  <div className="InputData">
                    <input type="date" value={formData.entryDate} onChange={(e) => setFormData((prev) => ({ ...prev, entryDate: e.target.value }))} className="Input" />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Dias em Oficina Externa</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="text"
                      name="externalShopDays"
                      value={formData.externalShopDays ?? ''}
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) setFormData((prev) => ({ ...prev, externalShopDays: e.target.value === '' ? undefined : parseInt(e.target.value, 10) }));
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Row 6: RENAVAM, N° BO */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Renavam</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" name="renavam" value={formData.renavam ?? ''} onChange={handleChange} />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">N° do Boletim de Ocorrência</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" name="numeroBo" value={formData.numeroBo ?? ''} onChange={handleChange} />
                  </div>
                </div>
              </div>

              {/* Row 7: Dados do proprietário (associado ou terceiro) */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo" title="Nome do proprietário (associado ou terceiro)">Nome do proprietário</div>
                  <div className="CampoInput">
                    <input className="Input" type="text" name="ownerName" value={formData.ownerName ?? ''} onChange={handleChange} />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Telefone do proprietário</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="tel"
                      placeholder="(21) 99999-8888"
                      value={formData.ownerPhone ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, ownerPhone: formatPhoneBr(e.target.value) }))}
                    />
                  </div>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">IMEI do rastreador</div>
                  <div className="CampoInput">
                    <input
                      className="Input"
                      type="text"
                      placeholder="359510086137028"
                      value={formData.rastreadorImei ?? ''}
                      inputMode="numeric"
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          rastreadorImei: e.target.value.replace(/\D/g, '').slice(0, 17),
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Row 8: Observações do atendimento (at-54) */}
              <div className="Frame1000003994">
                <div className="Campo">
                  <div className="CampoTitulo">Observações do Cliente</div>
                  <div className="CampoInput observacoes-campo">
                    <textarea
                      className="Input observacoes-textarea"
                      name="observacoesCliente"
                      rows={3}
                      maxLength={OBSERVACOES_MAX_LENGTH}
                      value={formData.observacoesCliente ?? ''}
                      onChange={handleChange}
                    />
                  </div>
                  <span className="observacoes-hint">
                    O que o cliente relatou ou pediu. {OBSERVACOES_MAX_LENGTH - (formData.observacoesCliente ?? '').length} caracteres restantes.
                  </span>
                </div>
                <div className="Campo">
                  <div className="CampoTitulo">Observações da Oficina</div>
                  <div className="CampoInput observacoes-campo">
                    <textarea
                      className="Input observacoes-textarea"
                      name="observacoesOficina"
                      rows={3}
                      maxLength={OBSERVACOES_MAX_LENGTH}
                      value={formData.observacoesOficina ?? ''}
                      onChange={handleChange}
                    />
                  </div>
                  <span className="observacoes-hint">
                    Anotações internas da equipe. {OBSERVACOES_MAX_LENGTH - (formData.observacoesOficina ?? '').length} caracteres restantes.
                  </span>
                </div>
              </div>

            </div>

            {/* Actions */}
            <div className="Frame1000003998">
              <div className="CtaBigEditar cancel" onClick={onCancel}>
                <button className="CustomFileCancel" type="button">Cancelar</button>
              </div>
              <button className="CtaBigEditar save" type="submit" disabled={isLoading}>Salvar</button>
            </div>
          </form>
        </div>
      </div>

      <FipePickerModal
        isOpen={showBrandPicker}
        title="Selecionar Marca"
        searchPlaceholder="Buscar marca..."
        fetchFn={fetchBrands}
        onSelect={handleBrandSelect}
        onCancel={() => setShowBrandPicker(false)}
      />
      <FipePickerModal
        isOpen={showModelPicker}
        title="Selecionar Modelo"
        searchPlaceholder="Buscar modelo..."
        fetchFn={fetchModels}
        onSelect={handleModelSelect}
        onCancel={() => setShowModelPicker(false)}
      />
    </div>
  );
};

export default EditCarModal;
